import React, { useCallback, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    Theme,
    withStyles,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';

import Spin from 'components/atoms/Spin/Spin';
import { fetchRegionsDict } from '../../api/regions';
import { DictionaryDto } from '../../api/dictionary';
import { getUserBranches } from '../../api/branches';
import { NPODto, syncNPOCount } from '../../api/npo';
import NPOCourierDialog from '../../components/organisms/NPOCourierDialog/NPOCourierDialog';

export const useStyles = makeStyles((theme: Theme) => ({
    formControl: {
        minWidth: 120,
        width: '100%',
    },
    checkbox: {
        padding: 0,
        margin: '0 8px 0 0',
    },
    buttons: {
        '& button': {
            marginRight: theme.spacing(1),
            '&:last-child': {
                marginRight: 0,
            },
        },
    },
    btn: {
        marginTop: theme.spacing(2),
    },
    actionButton: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        position: 'relative',
        transition: 'all .3s ease-in',
    },
    filterRow: {
        marginTop: theme.spacing(1),
    },
    tableRow: {
        cursor: 'pointer',
    },
}));

const StyledCell = withStyles(() => ({
    root: {
        borderBottom: '1px solid #b3b3b3',
    },
}))(TableCell);

const NPO = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [items, setItems] = useState<NPODto[]>([]);
    const [selected, setSelected] = React.useState<number[]>([]);
    const [selectedItems, setSelectedItems] = useState<NPODto[]>([]);

    const [loading, setLoading] = useState(false);

    const [regionId, setRegionId] = useState<number | string>('');
    const [courierDialogOpen, setCourierDialogOpen] = useState(false);
    const [regions, setRegions] = useState<DictionaryDto[]>([]);
    const [branchId, setBranchId] = useState<number | string>('');
    const [branches, setBranches] = useState<DictionaryDto[] | null>([]);
    const [depCode, setDepCode] = useState<string>('');

    const handleClick = useCallback(
        (item: NPODto) => {
            if (typeof item.count === 'undefined' || item.count === 0) {
                return;
            }
            const selectedIndex = selected.indexOf(item.id);
            let newSelected: number[] = [];
            let newSelectedItems: NPODto[] = [];

            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, item.id);
                newSelectedItems = newSelectedItems.concat(selectedItems, item);
            } else {
                newSelected = selected.filter((s) => s !== item.id);
                newSelectedItems = selectedItems.filter((s) => s.id !== item.id);
            }

            setSelected(newSelected);
            setSelectedItems(newSelectedItems);
        },
        [selected, selectedItems]
    );

    const handleBranchChange = useCallback(
        (e: any) => {
            const value = e.target.value as string;
            const currentBranch =
                branches !== null && branches.find((branch) => branch.id === value);
            setBranchId(value);
            if (currentBranch) {
                setDepCode(currentBranch.code);
            }

            setItems([]);
            setSelected([]);
            setSelectedItems([]);
        },
        [branches]
    );

    const getBranchesByRegionCode = useCallback(
        async (regionCode: number | string) => {
            try {
                const branchList = await getUserBranches(regionCode as string);
                if (branchList?.length) {
                    setBranches(
                        branchList.map(
                            (b) =>
                                ({ id: b.id, description: b.title, code: b.code } as DictionaryDto)
                        )
                    );
                } else {
                    setBranches(null);
                }

                if (branchList.length === 1) {
                    setBranchId(branchList[0].id as number);
                    setDepCode(branchList[0].code);
                }
            } catch (e) {
                enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            }
        },
        [enqueueSnackbar]
    );
    const loadBranches = useCallback(
        async (regionId: number | string) => {
            const regionCode = regions.find((r) => r.id === regionId)?.code;
            if (!regionCode) {
                return;
            }

            await getBranchesByRegionCode(regionCode);
        },
        [getBranchesByRegionCode, regions]
    );

    const handleRegionChange = useCallback(
        async (e: any) => {
            const value = e.target.value as string;
            setBranchId('');
            setDepCode('');
            if (value) {
                setRegionId(value);
            }

            await loadBranches(value);

            setItems([]);
            setSelected([]);
            setSelectedItems([]);
        },
        [loadBranches]
    );
    const getDictionaries = useCallback(async () => {
        try {
            const regionList = await fetchRegionsDict();
            setRegions(regionList);
            if (regionList.length === 1) {
                const [currentRegion] = regionList;
                setRegionId(currentRegion.id);
                await getBranchesByRegionCode(currentRegion.code);
            }
        } catch (e) {
            enqueueSnackbar(`Ошибка при загрузке справочников: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        }
    }, [getBranchesByRegionCode, enqueueSnackbar]);

    const getAll = useCallback(async () => {
        if (!regionId || !depCode) {
            return;
        }

        setLoading(true);
        try {
            if (depCode) {
                const data = await syncNPOCount(depCode);
                setItems(data);
            }
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [regionId, enqueueSnackbar, depCode]);

    // @ts-ignore
    const handleSearch = useCallback(async () => {
        await getAll();
    }, [getAll]);
    const handleCourierDialogClose = useCallback(() => {
        setCourierDialogOpen(false);
    }, []);

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = items.map((n) => n.id);
            setSelected(newSelected);
            setSelectedItems(items);
            return;
        }

        setSelected([]);
        setSelectedItems([]);
    };

    const refreshItems = useCallback(() => {
        setSelected([]);
        setSelectedItems([]);
        getAll();
    }, [getAll]);

    useEffect(() => {
        void getDictionaries();
    }, [getDictionaries]);

    useEffect(() => {
        if (regions.length === 0 || (regions.length === 1 && !regionId)) {
            return;
        }

        if (
            (regions.length === 1 && branches?.length === 0) ||
            (branches?.length === 1 && !branchId)
        ) {
            return;
        }

        getAll();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regionId, branchId, regions, branches]);
    // @ts-ignore
    return (
        <Box position={'relative'}>
            <Grid container spacing={3}>
                <Grid item xs={6} md={4} xl={4} lg={4}>
                    <FormControl
                        className={classes.formControl}
                        variant="outlined"
                        error={!regionId}
                    >
                        <InputLabel id="product-id-label">Регион</InputLabel>
                        <Select
                            labelId="product-id-label"
                            id="product-id"
                            value={regionId}
                            label="Регион"
                            onChange={handleRegionChange}
                        >
                            {regions.length > 1 && (
                                <MenuItem value="">
                                    <em>Не выбрано</em>
                                </MenuItem>
                            )}

                            {regions.map((region, index) => (
                                <MenuItem key={index} value={region.id}>
                                    {region.description}
                                </MenuItem>
                            ))}
                        </Select>
                        {!regionId && <FormHelperText>Выберите регион</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item xs={6} md={4} xl={4} lg={4}>
                    <FormControl
                        className={classes.formControl}
                        disabled={branches?.length === 1}
                        variant="outlined"
                    >
                        <InputLabel id="branch-id-label">Отделение</InputLabel>
                        <Select
                            labelId="branch-id-label"
                            id="branch-id"
                            value={branchId}
                            label="Отделение"
                            onChange={handleBranchChange}
                        >
                            {branches?.length && (
                                <MenuItem value="">
                                    <em>Не выбрано</em>
                                </MenuItem>
                            )}

                            {branches?.map((branch, index) => (
                                <MenuItem key={index} value={branch.id}>
                                    {branch.description}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container justify="space-between" className={classes.buttons}>
                <Grid item>
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="primary"
                                indeterminate={
                                    selected.length > 0 && selected.length < items.length
                                }
                                checked={items.length > 0 && selected.length === items.length}
                                onChange={handleSelectAllClick}
                                inputProps={{
                                    'aria-label': 'select all desserts',
                                }}
                            />
                        }
                        label="Выбрать все"
                    />
                </Grid>
                <Grid item style={{ marginLeft: 'auto' }}>
                    <Box
                        display="flex"
                        justifyContent="end"
                        position="relative"
                        id="action-buttons"
                        minWidth={40}
                        minHeight={60}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.btn}
                            onClick={handleSearch}
                            startIcon={<Search />}
                        >
                            Поиск
                        </Button>
                        <Button
                            disabled={selected.length === 0}
                            variant="contained"
                            color="primary"
                            className={[classes.actionButton, classes.btn].join(' ')}
                            onClick={() => setCourierDialogOpen(true)}
                        >
                            Назначить
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            <Box position={'relative'} overflow="auto">
                <Box display="flex">
                    <Box flex={4}>
                        {loading && <Spin variant="inline" />}
                        <Box position={'relative'}>
                            {loading && <Spin />}
                            <TableContainer>
                                <Table size={'small'} aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell>№</TableCell>
                                            <TableCell>Код</TableCell>
                                            <TableCell>Описание</TableCell>
                                            <TableCell>Количество</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item, index) => {
                                            const isSelected = selected.indexOf(item.id) !== -1;
                                            return (
                                                <TableRow
                                                    className={classes.tableRow}
                                                    key={index}
                                                    hover
                                                    onClick={() => handleClick(item)}
                                                    selected={isSelected}
                                                >
                                                    <StyledCell>
                                                        <Checkbox
                                                            color="primary"
                                                            className={classes.checkbox}
                                                            checked={isSelected}
                                                            inputProps={{
                                                                'aria-labelledby': `enhanced-table-checkbox-${index}`,
                                                            }}
                                                        />
                                                    </StyledCell>
                                                    <StyledCell>{item.id}</StyledCell>
                                                    <StyledCell>{item.code}</StyledCell>
                                                    <StyledCell>{item.description}</StyledCell>
                                                    <StyledCell>{item.count}</StyledCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <NPOCourierDialog
                open={courierDialogOpen}
                onClose={handleCourierDialogClose}
                parentRegionId={regionId}
                selectedItems={selectedItems}
                dep_code={depCode}
                refreshList={refreshItems}
                formFields={selectedItems.reduce((acc, selectedItem) => {
                    return { ...acc, [selectedItem.code]: '' };
                }, {})}
            />
        </Box>
    );
};

export default NPO;
